import { useMemo } from 'react';

import api, { snakeToCamel } from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { useQuery } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import { getRouteApi } from '@mint/routing';

import { queryClient } from '../api/queries/client.js';
import { GET_BUILDER_TABLE_VIEW } from '../constants/EndpointConstants.js';
import {
  beyondWayColumnsUnderFf,
  beyondWayColumnsUnderFfPart2,
} from '../constants/myCampaigns.js';
import { QueryKey } from '../constants/queryKeys.js';
import {
  ADSERVER_COST,
  DSP_COST,
} from '../features/EditModal/EditModal.constants.js';
import {
  BuilderSchemaResponse,
  BuilderSchemaResponseSchema,
} from '../types/builderSchema.js';
import { prepareDimensionsData } from '../utils/campaigns/myCampaigns.js';

const getBuilderSchemaData = async (campaignId: string) => {
  const response = await api.get(`${GET_BUILDER_TABLE_VIEW}dimensions/`, {
    params: { campaign_collection_id: campaignId },
  });

  return BuilderSchemaResponseSchema.parse(response.data);
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

type Options = {
  skip?: boolean;
};

export const useGetBuilderSchemaData = (options?: Options) => {
  const params = routeApi.useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: [QueryKey.BUILDER_SCHEMA, params.campaignId],
    queryFn: () => getBuilderSchemaData(params.campaignId),
    enabled: !options?.skip && !!params.campaignId,
    staleTime: Infinity,
  });

  return { data, isLoading, isError };
};

export const usePreparedBuilderSchema = () => {
  const ff = useFlags('@mint/arm', [
    'beyondWayColumnsBuyingPlan',
    'beyondWayColumnsBuyingPlanPart2',
  ]);
  const { t } = useTranslation('@myn/planning');
  const params = routeApi.useParams();
  const isAdServerCostAccess = useIsAllowedPermission(
    'adserverCostFeature:view',
  );
  const isDspCostAccess = useIsAllowedPermission('dpsCostFeature:view');

  const data = queryClient.getQueryData<BuilderSchemaResponse>([
    QueryKey.BUILDER_SCHEMA,
    params.campaignId,
  ]);

  const dimensionList = useMemo(() => {
    return data ? prepareDimensionsData({ ...data, t }) : [];
  }, [data]);

  const dimensionListWithAccess = useMemo(() => {
    const blockedByCostAccess = !isAdServerCostAccess || !isDspCostAccess;
    const blockedBeyondWay = !ff.beyondWayColumnsBuyingPlan.enabled;
    const blockedBeyondWayPart2 = !ff.beyondWayColumnsBuyingPlanPart2.enabled;

    return dimensionList.map((group) => {
      const filteredDimensions = group.dimensions.filter((dimension) => {
        const valueCamel = snakeToCamel(dimension.value);

        if (
          blockedByCostAccess &&
          (dimension.value === ADSERVER_COST.DIM ||
            dimension.value === DSP_COST.DIM)
        ) {
          return false;
        }

        if (blockedBeyondWay && beyondWayColumnsUnderFf.includes(valueCamel)) {
          return false;
        }

        if (
          blockedBeyondWayPart2 &&
          beyondWayColumnsUnderFfPart2.includes(valueCamel)
        ) {
          return false;
        }

        return true;
      });

      return {
        ...group,
        dimensions: filteredDimensions,
      };
    });
  }, [
    dimensionList,
    isAdServerCostAccess,
    isDspCostAccess,
    ff.beyondWayColumnsBuyingPlan.enabled,
    ff.beyondWayColumnsBuyingPlanPart2.enabled,
  ]);

  const customColumns = useMemo(() => {
    return (
      dimensionListWithAccess.find((item) => item.isCustom)?.dimensions ?? []
    );
  }, [dimensionListWithAccess]);
  const otherTechCosts = useMemo(() => {
    return (
      dimensionListWithAccess.find((item) => item.isTechCost)?.dimensions ?? []
    );
  }, [dimensionListWithAccess]);

  return {
    builderSchema: dimensionListWithAccess,
    customColumns,
    otherTechCosts,
  };
};
